/**
 * Filtro com pesquisa — o padrão para filtros com MUITAS opções (entidades,
 * serviços, promotores, lojas...). Um `TextField select` com centenas de itens
 * cobre o ecrã inteiro e obriga a fazer scroll à procura; aqui escreve-se.
 *
 * - escrever filtra, sem distinguir maiúsculas nem acentos («agua» → «Água Brava»)
 * - a lista mostra no máximo `maxResultados` e tem altura fixa com scroll
 * - vazio = sem filtro (o rótulo «Todas»/«Todos» aparece como placeholder),
 *   com botão para limpar
 *
 * Para listas curtas e fixas (estado, método, origem) continua a usar-se o select.
 *
 * Valor: string; `valorTodos` (por omissão 'all') representa «sem filtro», para
 * encaixar nos estados que as páginas já têm.
 */
import React from 'react';
import { Autocomplete, TextField, Typography, createFilterOptions } from '@mui/material';

export interface FilterOption {
  value: string;
  label: string;
}

interface Props {
  label: string;
  value: string;
  onChange: (value: string) => void;
  /** Strings simples (valor = rótulo) ou pares valor/rótulo. */
  options: Array<string | FilterOption>;
  /** Texto quando não há filtro: «Todas», «Todos»... */
  placeholderTodos?: string;
  valorTodos?: string;
  maxResultados?: number;
  disabled?: boolean;
}

const FilterAutocomplete: React.FC<Props> = ({
  label,
  value,
  onChange,
  options,
  placeholderTodos = 'Todas',
  valorTodos = 'all',
  maxResultados = 50,
  disabled,
}) => {
  const opcoes: FilterOption[] = React.useMemo(
    () => options.map((o) => (typeof o === 'string' ? { value: o, label: o } : o)),
    [options],
  );
  const seleccionada = value === valorTodos ? null : opcoes.find((o) => o.value === value) ?? null;

  // Por omissão o MUI já ignora maiúsculas e acentos.
  const filtrar = React.useMemo(() => createFilterOptions<FilterOption>({ stringify: (o) => o.label }), []);
  // Quantas opções casam com o que se escreveu — para avisar quando a lista foi cortada.
  const casam = React.useRef(0);

  // Se a lista foi cortada, diz-se que é preciso escrever — senão parece que acaba ali.
  const Lista = React.useMemo(() => {
    const L = React.forwardRef<HTMLUListElement, React.HTMLAttributes<HTMLElement>>((p, ref) => (
      <ul ref={ref} {...p}>
        {p.children}
        {casam.current > maxResultados && (
          <li style={{ padding: '6px 16px', fontSize: 12, color: '#888', listStyle: 'none' }}>
            A mostrar {maxResultados} de {casam.current} — escreva para procurar
          </li>
        )}
      </ul>
    ));
    L.displayName = 'FilterAutocompleteLista';
    return L;
  }, [maxResultados]);

  return (
    <Autocomplete
      size="small"
      fullWidth
      disabled={disabled}
      options={opcoes}
      value={seleccionada}
      onChange={(_, nova) => onChange(nova ? nova.value : valorTodos)}
      getOptionLabel={(o) => o.label}
      isOptionEqualToValue={(a, b) => a.value === b.value}
      filterOptions={(lista, estado) => {
        const todas = filtrar(lista, estado);
        casam.current = todas.length;
        return todas.slice(0, maxResultados);
      }}
      noOptionsText="Sem resultados"
      slotProps={{ listbox: { style: { maxHeight: 280 } } }}
      renderInput={(params) => (
        <TextField
          {...params}
          label={label}
          placeholder={seleccionada ? undefined : placeholderTodos}
          slotProps={{ inputLabel: { shrink: true } }}
        />
      )}
      renderOption={(props, o) => {
        const { key, ...resto } = props as React.HTMLAttributes<HTMLLIElement> & { key: React.Key };
        return (
          <li key={key} {...resto}>
            <Typography variant="body2" noWrap>{o.label}</Typography>
          </li>
        );
      }}
      ListboxComponent={Lista}
    />
  );
};

export default FilterAutocomplete;
